/**
 * The system prompts of the eval judges, with every version: a tree on the left (each
 * judge, and the versions of the selected one under it), the selected version on the
 * right. The versions here are the only copy of the texts; the suite code names the judges
 * only. A run records the versions that are current when it is queued, so old runs never
 * change. The selected judge and version are in the query string, so a view can be shared.
 */
import { ArrowLeft, Bot, ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { JudgePromptCard } from './components/judge/JudgePromptCard';
import { ShowArchivedToggle } from './components/VersionArchiveControl';
import { useDefaultSuite } from './hooks/useDefaultSuite';
import { evalsService } from './services/evalsService';
import type { JudgePromptInfo, JudgePromptVersion } from './types/evalTypes';
import { formatDateTime } from './utils/evalFormat';

const judgeKey = (judge: Pick<JudgePromptInfo, 'suiteId' | 'judgeName'>) => `${judge.suiteId}~${judge.judgeName}`;

/** Versions that wait for review: not archived, and newer than the current one. */
function pendingVersions(judge: JudgePromptInfo): number {
  const currentNumber = judge.versions.find((version) => version.versionId === judge.currentVersionId)?.number ?? 0;
  return judge.versions.filter((version) => !version.archived && version.number > currentNumber).length;
}

export const JudgePrompts = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [judges, setJudges] = useState<JudgePromptInfo[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setJudges(await evalsService.listJudgePrompts());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load the judge prompts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The default suite's judges first, so the URL-less view opens on one of them.
  const [defaultSuiteId] = useDefaultSuite();
  const ordered = useMemo(
    () => [...judges].sort((a, b) => Number(b.suiteId === defaultSuiteId) - Number(a.suiteId === defaultSuiteId)),
    [judges, defaultSuiteId],
  );

  const judge = judges.find((candidate) => candidate.suiteId === searchParams.get('suite') && candidate.judgeName === searchParams.get('judge')) ?? ordered[0];
  // The chosen version, else the current one, else the newest.
  const version: JudgePromptVersion | undefined =
    judge?.versions.find((candidate) => candidate.versionId === searchParams.get('version')) ??
    judge?.versions.find((candidate) => candidate.versionId === judge.currentVersionId) ??
    judge?.versions[0];

  const select = (target: JudgePromptInfo, versionId?: string) =>
    setSearchParams({ suite: target.suiteId, judge: target.judgeName, ...(versionId && { version: versionId }) }, { replace: true });

  /** Puts a changed judge into the list; `selectNewest` follows a version just saved. */
  const replace = (updated: JudgePromptInfo, selectNewest = false) => {
    setJudges((current) => current.map((candidate) => (judgeKey(candidate) === judgeKey(updated) ? updated : candidate)));
    if (selectNewest && updated.versions[0]) {
      select(updated, updated.versions[0].versionId);
    }
  };

  const totalArchived = judges.reduce((sum, candidate) => sum + candidate.versions.filter((entry) => entry.archived).length, 0);

  if (loading) return <LoadingSpinner message="Loading judge prompts…" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Link to="/evals" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
          <ArrowLeft size={14} /> Evals
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Judge prompts</h1>
          <p className="text-sm text-gray-500">
            Each judge's system prompt, with every version. Runs use the version that is current when they are queued; a new
            version never changes old runs. Versions from Claude have a robot icon and are not current until you make them
            current. Archive a version to reject it: give a reason, and Claude reads it.
          </p>
        </div>

        {error && <ErrorDisplay message={error} />}

        <div className="grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-6 items-start">
          <ul className="bg-white rounded-lg shadow-sm p-3 space-y-1">
            {totalArchived > 0 && (
              <li className="px-2 pb-2 border-b border-gray-100">
                <ShowArchivedToggle count={totalArchived} checked={showArchived} onChange={setShowArchived} />
              </li>
            )}
            {judges.length === 0 && <li className="text-sm text-gray-500">No judges.</li>}
            {ordered.map((candidate) => {
              const open = judge !== undefined && judgeKey(candidate) === judgeKey(judge);
              const current = candidate.versions.find((entry) => entry.versionId === candidate.currentVersionId);
              const pending = pendingVersions(candidate);
              const shown = candidate.versions.filter((entry) => showArchived || !entry.archived || entry.versionId === version?.versionId);
              return (
                <li key={judgeKey(candidate)}>
                  <button
                    type="button"
                    onClick={() => select(candidate)}
                    className={`w-full text-left rounded-lg px-2 py-2 ${open ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                  >
                    <span className="flex flex-wrap items-center gap-1.5">
                      {open ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                      <span className="font-mono text-sm font-medium text-gray-900">{candidate.judgeName}</span>
                      {pending > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{pending} to review</span>}
                    </span>
                    <span className="block pl-5 text-xs text-gray-600">
                      <span className="font-mono">{candidate.suiteId}</span> ·{' '}
                      {current ? `current v${current.number}` : <span className="text-red-700">no prompt</span>}
                    </span>
                  </button>

                  {open && (
                    <ul className="ml-4 mt-1 space-y-0.5 border-l border-gray-200 pl-2">
                      {shown.map((entry) => (
                        <li key={entry.versionId}>
                          <button
                            type="button"
                            onClick={() => select(candidate, entry.versionId)}
                            className={`w-full text-left rounded-md px-2 py-1 text-sm ${
                              entry.versionId === version?.versionId ? 'bg-blue-50 ring-1 ring-blue-400' : 'hover:bg-gray-50'
                            } ${entry.archived ? 'opacity-60' : ''}`}
                          >
                            <span className="flex items-center gap-1.5">
                              <span className="font-medium text-gray-900">v{entry.number}</span>
                              {entry.versionId === candidate.currentVersionId && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">current</span>
                              )}
                              {entry.archived && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">archived</span>}
                              {entry.tokenName !== undefined && <Bot size={12} className="text-violet-700" aria-label="Proposed by Claude" />}
                              <span className="ml-auto text-[11px] text-gray-500">{formatDateTime(entry.createdAt)}</span>
                            </span>
                            {entry.note && <span className="block text-xs text-gray-700 truncate">{entry.note}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          {judge && (
            <JudgePromptCard
              key={`${judgeKey(judge)}~${version?.versionId ?? 'none'}`}
              judge={judge}
              version={version}
              currentUid={user?.uid}
              onCreate={async (draft) => replace(await evalsService.createJudgePromptVersion(judge.suiteId, judge.judgeName, draft), true)}
              onMakeCurrent={async (versionId) => replace(await evalsService.setCurrentJudgePrompt(judge.suiteId, judge.judgeName, versionId))}
              onSetVersionArchived={async (versionId, archived, reason) =>
                replace(await evalsService.setJudgePromptVersionArchived(judge.suiteId, judge.judgeName, versionId, archived, reason))
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
